import { useEffect, useState } from "react";

import LazySearchSelect from "../../../components/shared/LazySearchSelect";
import { Checkbox } from "../../../components/ui/checkbox.jsx";
import { Input } from "../../../components/ui/input.jsx";
import { Label } from "../../../components/ui/label.jsx";
import { ScrollArea } from "../../../components/ui/scroll-area.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select.jsx";
import { useApi } from "../../../context/ApiContext";
import { useAuth } from "../../../context/AuthContext";

const UserForm = ({ formData, setFormData, isEdit = false }) => {
  const { user: currentUser } = useAuth();
  const api = useApi();
  const [roles, setRoles] = useState([]);
  const [projects, setProjects] = useState([]);

  const isSuperAdmin = currentUser?.role === "super_admin" || currentUser?.role?.name === "super_admin";

  useEffect(() => {
    // If not super admin and creating a new user, set the tenantId to the current user's tenant
    if (!isSuperAdmin && !isEdit && !formData.tenantId && currentUser?.tenantId) {
      setFormData((prev) => ({ ...prev, tenantId: currentUser.tenantId }));
    }
  }, [isSuperAdmin, isEdit, currentUser, setFormData, formData.tenantId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rolesRes, projectsRes] = await Promise.all([api.get("/roles/all"), api.get("/projects/all")]);
        setRoles(rolesRes.data);
        setProjects(projectsRes.data);
      } catch (error) {
        console.error("Failed to fetch form data:", error);
      }
    };
    fetchData();
  }, [api]);

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value
    }));
  };

  const handleRoleChange = (value) => {
    setFormData((prev) => ({ ...prev, role: value }));
  };

  const handleProjectToggle = (projectId, checked) => {
    setFormData((prev) => {
      const currentProjects = prev.allowedProjectIds || [];
      const newProjects = checked ? [...currentProjects, projectId] : currentProjects.filter((id) => id !== projectId);
      return { ...prev, allowedProjectIds: newProjects };
    });
  };

  const handleTenantChange = (value) => {
    setFormData((prev) => ({ ...prev, tenantId: value }));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name</Label>
          <Input id="name" value={formData.name || ""} onChange={handleChange} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={formData.email || ""} onChange={handleChange} required />
        </div>
      </div>

      {!isEdit && (
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={formData.password || ""} onChange={handleChange} required />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input id="phone" value={formData.phone || ""} onChange={handleChange} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="role">Role</Label>
          <Select value={formData.role} onValueChange={handleRoleChange}>
            <SelectTrigger id="role">
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {roles.map((role) => (
                <SelectItem key={role.id} value={role.id}>
                  {role.name
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(" ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="space-y-2">
          <Label htmlFor="tenantId">
            Assign to Tenant <span className="text-red-500">*</span>
          </Label>
          <LazySearchSelect
            value={formData.tenantId}
            onChange={handleTenantChange}
            apiUrl="/tenants"
            placeholder="Select a tenant"
            searchPlaceholder="Search tenants..."
            labelKey="name"
            valueKey="id"
            disableInitialFetch={true}
            renderItem={(t) => (
              <div className="flex flex-col min-w-0">
                <span className="font-medium truncate">{t.name}</span>
                <span className="text-[10px] text-slate-500 font-mono truncate">{t.tenantKey}</span>
              </div>
            )}
          />
          <p className="text-xs text-slate-500">Select which tenant this user belongs to.</p>
        </div>
      )}

      <div className="space-y-3">
        <Label className="text-sm font-semibold">Allowed Projects Access</Label>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          User will only be able to see data for the selected projects. Leave empty for no access (or select all for full access).
        </p>
        <div className="border rounded-xl p-4 bg-slate-50/50 dark:bg-yale-blue-900/20 dark:border-yale-blue-800">
          <ScrollArea className="h-[150px] pr-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-white dark:hover:bg-yale-blue-800 transition-colors"
                >
                  <Checkbox
                    id={`project-${project.id}`}
                    checked={formData.allowedProjectIds?.includes(project.id)}
                    onCheckedChange={(checked) => handleProjectToggle(project.id, checked)}
                  />
                  <Label
                    htmlFor={`project-${project.id}`}
                    className="text-sm font-medium cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {project.name}
                  </Label>
                </div>
              ))}
              {projects.length === 0 && <p className="text-sm text-slate-400 col-span-2 text-center py-4">No projects found.</p>}
            </div>
          </ScrollArea>
        </div>
      </div>

      {isEdit && (
        <div className="flex items-center space-x-2 py-2">
          <input
            type="checkbox"
            id="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            className="h-4 w-4 rounded border-gray-300 dark:border-yale-blue-700 text-yale-blue-600 dark:bg-yale-blue-950 focus:ring-primary"
          />
          <Label htmlFor="isActive">Active Account</Label>
        </div>
      )}
    </div>
  );
};

export default UserForm;
